import { BreakpointObserver, BreakpointState } from '@angular/cdk/layout'
import { AsyncPipe } from '@angular/common'
import { HttpClient } from '@angular/common/http'
import { Component, HostListener, inject, OnDestroy, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core'
import { MatIconButton } from '@angular/material/button'
import { MatDialog } from '@angular/material/dialog'
import { MatIcon } from '@angular/material/icon'
import { MatDrawer, MatDrawerContainer } from '@angular/material/sidenav'
import { MatTooltip } from '@angular/material/tooltip'
import { Title } from '@angular/platform-browser'
import { ActivatedRoute, Router, RouterLink } from '@angular/router'
import { TranslateDirective, TranslatePipe } from '@ngx-translate/core'
import { APP_NAME, formatPageTitle } from '../app-title-strategy'
import { AngularSplitModule } from 'angular-split'
import { filter, forkJoin, map, mergeMap, of, switchMap, take, tap } from 'rxjs'
import { MeetingDetailsBarComponent } from '../meeting-details-bar/meeting-details-bar.component'
import { MeetingInfoComponent } from '../meeting-info/meeting-info.component'
import { MeetingResourcesContainerComponent } from '../meeting-resources-container/meeting-resources-container.component'
import { MeetingTimelineDialogComponent } from '../meeting-timeline-dialog/meeting-timeline-dialog.component'
import {
  Meeting,
  MeetingsService,
  canDownloadMinutes,
  canResolveAnyThread,
  canUserEdit,
  canUserSuggest,
  hasCommentAccess,
  hasEditAccess,
  isDraftMeeting,
  MetricType,
} from '../meetings.service'
import { ExportDialogComponent, ExportDialogInputData, ExportDialogOutputData } from '../export-dialog/export-dialog.component'
import { LoggerService } from '../logger.service'
import { MetricsService } from '../metrics.service'
import { SpinnerComponent } from '../spinner/spinner.component'
import { ToastService } from '../toast.service'
import { ToolbarComponent } from '../toolbar/toolbar.component'
import { UsersService } from '../users.service'
import { BannerComponent } from '../banner/banner.component'
import { ConfirmDialogComponent } from '../confirm-dialog/confirm-dialog.component'
import { ComponentWithUnsavedChanges } from '../guards/unsaved-changes.guard'
import { MeetingOutputsContainerComponent } from './meeting-outputs-container/meeting-outputs-container.component'
import { EditorSettingsService } from './editor-settings.service'
import { ShareMeetingDialogComponent } from './share-meeting-dialog/share-meeting-dialog.component'
import { TemplatesService } from '../templates.service'
import { ActionItemsService } from './action-items/action-items.service'

@Component({
  selector: 'app-meeting',
  standalone: true,
  imports: [
    AsyncPipe,
    BannerComponent,
    SpinnerComponent,
    TranslatePipe,
    TranslateDirective,
    AngularSplitModule,
    MatDrawer,
    MatDrawerContainer,
    MatIcon,
    MatIconButton,
    MatTooltip,
    MeetingDetailsBarComponent,
    MeetingInfoComponent,
    ToolbarComponent,
    MeetingOutputsContainerComponent,
    MeetingResourcesContainerComponent,
    RouterLink,
  ],
  templateUrl: './meeting-page.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './meeting-page.component.scss',
})
export class MeetingPageComponent implements OnInit, OnDestroy, ComponentWithUnsavedChanges {
  router = inject(Router)
  route = inject(ActivatedRoute)
  logger = inject(LoggerService)
  dialog = inject(MatDialog)
  http = inject(HttpClient)
  meetingsService = inject(MeetingsService)
  actionItemsService = inject(ActionItemsService)
  usersService = inject(UsersService)
  toastService = inject(ToastService)
  metricsService = inject(MetricsService)
  breakpointObserver = inject(BreakpointObserver)
  templatesService = inject(TemplatesService)
  editorSettingsService = inject(EditorSettingsService)
  titleService = inject(Title)

  @ViewChild(MeetingResourcesContainerComponent)
  meetingResourcesContainer: MeetingResourcesContainerComponent | undefined = undefined

  @HostListener('window:beforeunload', ['$event'])
  onBeforeUnload(event: BeforeUnloadEvent) {
    if (this.hasUnsavedChanges) {
      event.preventDefault()
    }
  }

  get hasUnsavedChanges(): boolean {
    return this.meetingResourcesContainer?.hasUnsavedTemplateChanges ?? false
  }

  meetingId$ = this.route.params.pipe(
    map((params) => (typeof params['id'] === 'string' ? parseInt(params['id'], 10) : null)),
    filter((id) => id !== null)
  )

  meeting$ = this.meetingId$.pipe(
    switchMap((id) => this.meetingsService.getMeeting(id!)),
    tap((result) => {
      if (result.isSuccess) {
        this.metricsService.sendMetric(result.data.id, MetricType.VIEW_MEETING)
        const name = result.data.name
        const truncatedName = name.length > 20 ? `${name.slice(0, 20)}…` : name
        this.titleService.setTitle(formatPageTitle(truncatedName))
        if (result.data.isUpcoming || result.data.isOngoing || isDraftMeeting(result.data)) {
          this.router.navigate(['/app', 'meetings', result.data.id, 'setup'], { replaceUrl: true })
        }
      }
      if (result.isError && this.currentUser$.value?.role === 'SUPPORT') {
        const meetingId = this.route.snapshot.params['id']
        this.router.navigate(['/app', 'support', 'meetings', meetingId])
      }
    })
  )

  // A Restricted user never sees the template, and the endpoint is not open to them, so skip the query for them.
  // Reuses the cached meeting query (deduped by the query client) to read access level without re-running meeting$ effects.
  templateItems$ = this.meetingId$.pipe(
    switchMap((meetingId) =>
      this.meetingsService.getMeeting(meetingId!).pipe(
        switchMap((meetingResult) => {
          if (!meetingResult.isSuccess || meetingResult.data.accessLevel === 'RESTRICTED') {
            return of([])
          }
          return this.templatesService.getTemplateItems(meetingId!).pipe(map((result) => (result.isSuccess ? result.data : [])))
        })
      )
    )
  )

  isInfoPanelOpen$ = this.route.queryParams.pipe(map((params) => params['isInfoPanelOpen'] === 'true'))
  isSensitiveModeEnabled$ = this.route.queryParams.pipe(map((params) => params['useSensitiveMode'] === 'true'))
  isEditorMaximized$ = this.editorSettingsService.isEditorMaximized$
  editorMinSize = 50
  editorMaxSize = 70
  editorSize = this.editorMinSize
  useVerticalMode = false
  currentUser$ = this.usersService.currentUser$
  resendAssistantMutation = this.meetingsService.resendAssistantMutation()
  switchToBrowserAssistantMutation = this.meetingsService.switchToBrowserAssistantMutation()

  canUserEdit = canUserEdit

  userHasEditorAccess = hasEditAccess

  canUserComment = hasCommentAccess

  canUserSuggest = canUserSuggest

  canResolveAnyThread = canResolveAnyThread

  ngOnInit() {
    this.trackResponsiveBreakpoints()
    this.handleResendAssistantQueryParam()
    this.handleSendBrowserAssistantQueryParam()
  }

  ngOnDestroy() {
    this.titleService.setTitle(APP_NAME)
  }

  toggleSensitiveMode() {
    this.route.queryParams
      .pipe(
        map((params) => params['useSensitiveMode'] === 'true'),
        take(1)
      )
      .subscribe((isEnabled) => {
        this.router.navigate([], {
          relativeTo: this.route,
          queryParams: { useSensitiveMode: isEnabled ? null : true },
          queryParamsHandling: 'merge',
        })
      })
  }

  toggleInfoPanel() {
    this.route.queryParams
      .pipe(
        map((params) => params['isInfoPanelOpen'] === 'true'),
        take(1)
      )
      .subscribe((isOpen) => {
        this.router.navigate([], {
          relativeTo: this.route,
          queryParams: { isInfoPanelOpen: isOpen ? null : true },
          queryParamsHandling: 'merge',
          skipLocationChange: true,
        })
      })
  }

  exportMeeting(meeting: Meeting) {
    const dialogRef = this.dialog.open(ExportDialogComponent, {
      height: '365px',
      width: '450px',
      data: {
        allowTranscriptDownload: meeting.allowTranscriptDownload,
        canDownloadMinutes: canDownloadMinutes(meeting),
        allowOutlineDownload: meeting.allowOutlineDownload,
        hasTranscript: meeting.isTranscriptReady,
        hasOutline: meeting.isOutlineReady,
        hasMinutes: meeting.hasMinutes && meeting.hasEditorDocument,
      } as ExportDialogInputData,
    })
    const subscription = dialogRef.componentInstance.exportClicked
      .pipe(
        mergeMap((result: ExportDialogOutputData) => {
          const transcriptExport$ = result.includeTranscript ? this.meetingsService.exportTranscript(meeting, result.transcriptFormat) : of(null)
          const minutesExport$ = result.includeMinutes ? this.meetingsService.exportMinutes(meeting, result.minutesFormat) : of(null)
          const outlineExport$ = result.includeOutline ? this.meetingsService.exportOutline(meeting, result.outlineFormat) : of(null)
          const actionItemsExport$ = result.includeActionItems ? this.actionItemsService.export(meeting, result.actionItemsFormat) : of(null)
          return forkJoin([transcriptExport$, minutesExport$, outlineExport$, actionItemsExport$])
        })
      )
      .subscribe(() => {
        dialogRef.componentInstance.loading = false
        dialogRef.close()
      })
    dialogRef.afterClosed().subscribe(() => {
      subscription.unsubscribe()
    })
  }

  share(meeting: Meeting) {
    this.dialog.open(ShareMeetingDialogComponent, {
      width: '600px',
      maxWidth: '95vw',
      data: meeting,
      panelClass: 'share-meeting-dialog',
    })
  }

  showTimeline(meeting: Meeting) {
    this.dialog.open(MeetingTimelineDialogComponent, {
      width: '900px',
      maxHeight: '80vh',
      data: meeting,
      panelClass: 'timeline-dialog',
    })
  }

  private handleResendAssistantQueryParam() {
    this.route.queryParams.pipe(filter((params) => params['resendAssistant'] === 'true' || params['resendBot'] === 'true')).subscribe(() => {
      const meetingId = parseInt(this.route.snapshot.params['id'], 10)
      this.router.navigate([], {
        relativeTo: this.route,
        queryParams: { resendAssistant: null, resendBot: null },
        queryParamsHandling: 'merge',
        replaceUrl: true,
      })
      this.openResendAssistantDialog(meetingId)
    })
  }

  private openResendAssistantDialog(meetingId: number) {
    if (this.resendAssistantMutation.result().isPending) {
      return
    }
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: { title: 'MEETINGS.RESEND_ASSISTANT.TITLE', description: 'MEETINGS.RESEND_ASSISTANT.DESCRIPTION' },
    })
    dialogRef.afterClosed().subscribe(async (result: boolean) => {
      if (result) {
        await this.resendAssistantMutation.mutateAsync(meetingId)
      }
    })
  }

  private handleSendBrowserAssistantQueryParam() {
    this.route.queryParams.pipe(filter((params) => params['sendBrowserAssistant'] === 'true' || params['sendBrowserBot'] === 'true')).subscribe(() => {
      const meetingId = parseInt(this.route.snapshot.params['id'], 10)
      this.router.navigate([], {
        relativeTo: this.route,
        queryParams: { sendBrowserAssistant: null, sendBrowserBot: null },
        queryParamsHandling: 'merge',
        replaceUrl: true,
      })
      this.openSendBrowserAssistantDialog(meetingId)
    })
  }

  private openSendBrowserAssistantDialog(meetingId: number) {
    if (this.switchToBrowserAssistantMutation.result().isPending) {
      return
    }
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: { title: 'MEETINGS.SWITCH_TO_BROWSER_ASSISTANT.TITLE', description: 'MEETINGS.SWITCH_TO_BROWSER_ASSISTANT.DESCRIPTION' },
    })
    dialogRef.afterClosed().subscribe(async (result: boolean) => {
      if (result) {
        await this.switchToBrowserAssistantMutation.mutateAsync(meetingId)
      }
    })
  }

  private trackResponsiveBreakpoints() {
    this.breakpointObserver.observe(['(max-width: 1100px)']).subscribe((result: BreakpointState) => {
      this.useVerticalMode = result.matches
    })
  }

  constructBackButtonLink(meeting: Meeting): string[] {
    if (meeting.accessLevel === 'FULL_ACCESS') {
      return ['/app', 'meetings', 'past']
    }
    return ['/app', 'meetings', 'shared-with-me']
  }
}
